import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, AllCommunityModule, ModuleRegistry, themeBalham } from 'ag-grid-community';
import { format } from 'date-fns';
import * as XLSX from 'xlsx';
import { VShift } from '../../../utils/types/VShift';

ModuleRegistry.registerModules([AllCommunityModule]);

interface ShiftGridProps {
    shifts: VShift[];
}

const ShiftGrid: React.FC<ShiftGridProps> = ({ shifts }) => {
    const gridRef = useRef<AgGridReact>(null);

    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const [columnDefs] = useState<ColDef<VShift>[]>([
        {
            field: 'code',
            headerName: 'Code',
            pinned: isMobile ? undefined : 'left',
            sortable: true,
            filter: true,
        },
        {
            field: 'dateShift',
            headerName: 'Date Shift',
            sortable: true,
            filter: 'agDateColumnFilter',
            valueFormatter: (params: any) => (params.value ? format(new Date(params.value), 'dd/MM/yyyy') : ''),
        },
        {
            field: 'dateOuverture',
            headerName: 'Date Ouverture',
            sortable: true,
            filter: 'agDateColumnFilter',
            valueFormatter: (params: any) => (params.value ? format(new Date(params.value), 'dd/MM/yyyy') : ''),
        },
        {
            field: 'dateCloture',
            headerName: 'Date Clôture',
            sortable: true,
            filter: 'agDateColumnFilter',
            valueFormatter: (params: any) => (params.value ? format(new Date(params.value), 'dd/MM/yyyy') : ''),
        },
        { field: 'statut', headerName: 'Statut', sortable: true, filter: true },
        {
            field: 'cloture',
            headerName: 'Clôturé',
            sortable: true,
            filter: true,
            cellRenderer: (params: any) => (params.value ? 'Oui' : 'Non'),
        },
        {
            field: 'tauxFranc',
            headerName: 'Taux',
            sortable: true,
            filter: 'agNumberColumnFilter',
            valueFormatter: (params: any) => (params.value != null ? `${Number(params.value).toFixed(2).replace('.', ',')} CDF` : ''),
            cellStyle: { textAlign: 'right' },
        },
        {
            field: 'isDeleted',
            headerName: 'Supprimé',
            sortable: true,
            filter: true,
            cellRenderer: (params: any) => (params.value ? 'Oui' : 'Non'),
        },
    ]);

    const myTheme = themeBalham.withParams({ columnBorder: { style: 'dashed', width: '1px' } });

    const exportAsCsv = useCallback(() => {
        if (!gridRef.current) return;

        const visibleColumns = gridRef.current.api.getColumns()?.filter((col) => !col.getColDef().hide);
        if (!visibleColumns || visibleColumns.length === 0) {
            alert('Aucune colonne visible à exporter');
            return;
        }

        const columnKeys = visibleColumns.map((col) => col.getColId());
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');

        gridRef.current.api.exportDataAsCsv({
            fileName: `shifts_export_${timestamp}.csv`,
            allColumns: false,
            columnKeys,
            suppressQuotes: false,
        });
    }, []);

    const exportAsExcel = useCallback(() => {
        if (!gridRef.current) return;

        const visibleColumns = gridRef.current.api.getColumns()?.filter((col) => !col.getColDef().hide);
        if (!visibleColumns || visibleColumns.length === 0) {
            alert('Aucune colonne visible à exporter');
            return;
        }

        const rowData: VShift[] = [];
        gridRef.current.api.forEachNodeAfterFilterAndSort((node) => {
            rowData.push(node.data as VShift);
        });

        if (rowData.length === 0) {
            alert('Aucune donnée à exporter');
            return;
        }

        const exportData = rowData.map((row) => {
            const obj: Record<string, any> = {};
            visibleColumns.forEach((col) => {
                const colDef = col.getColDef();
                const field = colDef.field as string | undefined;
                const headerName = colDef.headerName ?? field ?? '';

                let value: any = field ? (row as any)[field] : '';

                // Format dates and numbers consistently
                if (field && (field.toLowerCase().includes('date') || field.toLowerCase().includes('shift'))) {
                    value = value ? format(new Date(value), 'dd/MM/yyyy') : '';
                } else if (typeof value === 'number') {
                    value = value != null ? Number(value).toFixed(2) : '';
                } else if (typeof value === 'boolean') {
                    value = value ? 'Oui' : 'Non';
                }

                obj[headerName] = value;
            });
            return obj;
        });

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Shifts');

        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        XLSX.writeFile(workbook, `shifts_export_${timestamp}.xlsx`);
    }, []);

    return (
        <>
            <div className="" style={{ height: '70vh', width: '100%' }}>
                <AgGridReact
                    ref={gridRef}
                    theme={myTheme}
                    getRowId={(params: any) => params.data.id?.toString()}
                    rowData={shifts}
                    columnDefs={columnDefs}
                    pagination={true}
                    domLayout="normal"
                    defaultColDef={{
                        resizable: true,
                        editable: false,
                        minWidth: 100,
                        flex: 1,
                    }}
                    onGridReady={(params: any) => {
                        // size columns to fit initial viewport
                        try {
                            params.api.sizeColumnsToFit();
                        } catch (e) {
                            // ignore if API not available yet
                        }
                    }}
                    suppressExcelExport={true}
                    enableCellTextSelection={true}
                    ensureDomOrder={true}
                    animateRows={true}
                    overlayNoRowsTemplate="<span style='padding: 10px;'>Aucune donnée ...</span>"
                />
            </div>

            <div className="dropdown mt-1">
                <button className="btn dropdown-toggle" type="button" id="exportDropdown" data-bs-toggle="dropdown">
                    Export
                </button>

                <ul className="dropdown-menu" aria-labelledby="exportDropdown">
                    <li>
                        <button className="dropdown-item" onClick={exportAsCsv}>
                            Export as CSV
                        </button>
                        <button className="dropdown-item" onClick={exportAsExcel}>
                            Export as Excel
                        </button>
                    </li>
                </ul>
            </div>
        </>
    );
};

export default ShiftGrid;
